// src/data/sectionBlocks.ts
// -------------------------
// Repository for the `section_blocks` table (Section Availability — the seasonal
// per-section request-wizard blocks, db-39). Confines all `.from("section_blocks")`
// access to the data layer; typed via getTypedClient(). The pure status /
// matching logic lives in src/services/sectionBlocks.ts.

import type { SectionBlock, SectionKey } from "../services/sectionBlocks";
import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

// One block row as stored (dates are `_date`-suffixed local YYYY-MM-DD strings).
export interface SectionBlockRow {
  id: string;
  section: SectionKey;
  start_date: string;
  end_date: string;
  message: string;
  deactivated: boolean;
  created_at: string;
}

// Typed as `string` (not a literal) so supabase-js doesn't deep-parse the select.
const BLOCK_COLS: string = "id, section, start_date, end_date, message, deactivated, created_at";

function localToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

// All blocks for the admin console, newest first. Includes Cancelled + Expired
// rows — they're the permanent history. Null on error / no client.
export async function listSectionBlocks(): Promise<SectionBlockRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("section_blocks")
    .select(BLOCK_COLS)
    .order("created_at", { ascending: false });
  if (error) {
    logger.warn("listSectionBlocks failed:", error.message);
    return null;
  }
  return (data as unknown as SectionBlockRow[]) ?? [];
}

// The blocks the wizard must honour today: not deactivated and today within the
// inclusive window. Mapped to the wizard's SectionBlock shape (start/end) so the
// pure matching logic (activeSectionBlock / stripBlockedSections) is unchanged.
// Null on error / no client.
export async function listLiveSectionBlocks(
  now: Date = new Date()
): Promise<SectionBlock[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const today = localToday(now);
  const { data, error } = await c
    .from("section_blocks")
    .select(BLOCK_COLS)
    .eq("deactivated", false)
    .lte("start_date", today)
    .gte("end_date", today);
  if (error) {
    logger.warn("listLiveSectionBlocks failed:", error.message);
    return null;
  }
  return ((data as unknown as SectionBlockRow[]) ?? []).map((r) => ({
    section: r.section,
    start: r.start_date,
    end: r.end_date,
    message: r.message,
  }));
}

type BlockInsert = Database["public"]["Tables"]["section_blocks"]["Insert"];
type BlockUpdate = Database["public"]["Tables"]["section_blocks"]["Update"];
type WriteResult = { error: { message: string } | null };

// Create a block (admin "Add a block"). Takes a loose object (admin form state)
// and casts to the generated Insert shape, per the repo write convention.
export async function insertSectionBlock(row: object): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("section_blocks").insert(row as BlockInsert);
  if (error) logger.warn("insertSectionBlock failed:", error.message);
  return { error };
}

// Edit a block's still-editable fields (start/end/message per the rules). The
// db-39 trigger enforces the locks + bumps updated_at. Returns { error }.
export async function updateSectionBlock(id: string, patch: object): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("section_blocks")
    .update(patch as BlockUpdate)
    .eq("id", id);
  if (error) logger.warn("updateSectionBlock failed:", error.message);
  return { error };
}

// Cancel a block (soft): flag it deactivated. It drops out of the wizard and
// stays in the list tagged Cancelled. Returns { error }.
export async function deactivateSectionBlock(id: string): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("section_blocks")
    .update({ deactivated: true } as BlockUpdate)
    .eq("id", id);
  if (error) logger.warn("deactivateSectionBlock failed:", error.message);
  return { error };
}
